import { type ReadonlyVec2, vec2 } from 'gl-matrix';

/**
 * These are shapes that exist from the ShapeTool - that is, in the shape picker of our
 * main toolbar bottom of screen.
 */
export enum ShapeToolShape {
  Rectangle = 1,
  Ellipse = 2,
  RoundedRectangle = 3,
  Diamond = 4,
  UpTriangle = 5,
  DownTriangle = 6,
  LeftParallelogram = 7,
  RightParallelogram = 8,
}

/**
 * These are shapes that do not exist in the ShapeTool.
 * For example, stickies, images, text, stickers all use the SharpRectangle
 * shape in their `ShapeComponent` in their ECS creators.
 */
export enum OtherShapes {
  SharpRectangle = 0,
}

export type ShapesType = ShapeToolShape | OtherShapes;

/**
 * As above, if we want to represent all the shapes that can be used in creating
 * a shape entity, we need an object which combines the shapes from the
 * ShapeTool, and also any of the other shapes.
 */
export const Shapes = {
  ...OtherShapes,
  ...ShapeToolShape,
};

export type RoundedPolygonShape = Exclude<ShapeToolShape, ShapeToolShape.Ellipse>;

/**
 * For WDF, Miro importing or sprites used for shapes in our main toolbar, a
 * well-defined string map between a shape's enum value and its string
 * identifier needs to be made. The following provides this mapping for just
 * `ShapeTool` objects for contexts such as Miro importing where we only
 * translate to `ShapeToolShapes` and do not use `OtherShapes`.
 */
export const shapeToolsToString = {
  [Shapes.Rectangle]: 'rectangle',
  [Shapes.Ellipse]: 'ellipse',
  [Shapes.RoundedRectangle]: 'rounded-rectangle',
  [Shapes.UpTriangle]: 'triangle',
  [Shapes.DownTriangle]: 'upside-down-triangle',
  [Shapes.Diamond]: 'diamond',
  [Shapes.LeftParallelogram]: 'left-parallelogram',
  [Shapes.RightParallelogram]: 'right-parallelogram',
} as const;

export const otherShapesToString = {
  [Shapes.SharpRectangle]: 'sharp-rectangle',
} as const;

/**
 * For contexts where a mapping between all shapes and their string identifier
 * is needed, the following is used.
 */
export const shapesToString = {
  ...shapeToolsToString,
  ...otherShapesToString,
} as const;

export type ShapeType =
  | (typeof shapeToolsToString)[keyof typeof shapeToolsToString]
  | (typeof otherShapesToString)[keyof typeof otherShapesToString];

export type ShapeConversion<
  AParam,
  AReturn = AParam,
  BParam = AReturn,
  BReturn = AParam
> = {
  contentToEntity: (input: AParam) => AReturn;
  entityToContent: (input: BParam) => BReturn;
};

export type Vec2ShapeConversions = ShapeConversion<
  vec2 | ReadonlyVec2,
  vec2,
  vec2 | ReadonlyVec2,
  vec2
>;

export interface ShapeConversions {
  offset: Vec2ShapeConversions;
  size: Vec2ShapeConversions;
}

/**
 * These are the dimensions (in world coordinates) for a shape as it is when dropped on the board
 */
export const shapeBoardSizes: Record<ShapeToolShape, vec2> = {
  [ShapeToolShape.Ellipse]: vec2.fromValues(160, 160),
  [ShapeToolShape.Rectangle]: vec2.fromValues(160, 160),
  [ShapeToolShape.RoundedRectangle]: vec2.fromValues(160, 160),
  [ShapeToolShape.UpTriangle]: vec2.fromValues(178, 166),
  [ShapeToolShape.DownTriangle]: vec2.fromValues(178, 166),
  [ShapeToolShape.Diamond]: vec2.fromValues(178, 178),
  [ShapeToolShape.RightParallelogram]: vec2.fromValues(166, 141),
  [ShapeToolShape.LeftParallelogram]: vec2.fromValues(166, 141),
};

export type MinSize = { minX: number; minY: number };

/**
 * These are the minimum dimensions (in world coordinates) for a shape as it is when dropped on the board
 * Used as a fallback for Miro bulk import
 */
export const fallbackMinShapeSizes: Record<ShapeToolShape, MinSize> = {
  [ShapeToolShape.Rectangle]: { minX: 45, minY: 49 },
  [ShapeToolShape.Ellipse]: { minX: 60, minY: 66 },
  [ShapeToolShape.RoundedRectangle]: { minX: 75, minY: 79 },
  [ShapeToolShape.Diamond]: { minX: 84, minY: 92 },
  [ShapeToolShape.UpTriangle]: { minX: 84, minY: 92 },
  [ShapeToolShape.DownTriangle]: { minX: 84, minY: 92 },
  [ShapeToolShape.LeftParallelogram]: { minX: 84, minY: 46 },
  [ShapeToolShape.RightParallelogram]: { minX: 84, minY: 46 },
};
